import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { apiService, ApiError } from '../../services/api';
import {
  Dispute,
  MultiRoundAccountDisputeRequest,
  MultiRoundInquiryDisputeRequest,
  MultiRoundLatesDisputeRequest,
  MultiRoundPersonalInfoDisputeRequest,
  Bureau,
  PersonalInfoCategory,
  AccountInformation,
  CreditInquiry,
  PersonalInformation,
} from '../../types/api';
import { Loader2, Check, Plus, X, CreditCard, Info } from 'lucide-react';

type DisputeType = 'account' | 'inquiry' | 'late' | 'personal-info';

interface UserDocument {
  id: string;
  userId: string;
  documentName: string;
  fileSize: number;
  mimeType: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

interface CreateDisputeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDisputeCreated: () => void;
  accounts: AccountInformation[];
  accountsGroupedById: Map<string, AccountInformation[]>;
  availableInquiries: CreditInquiry[];
  documents: UserDocument[];
  personalInformation: PersonalInformation[];
}

export function CreateDisputeDialog({
  isOpen,
  onOpenChange,
  onDisputeCreated,
  accounts,
  accountsGroupedById,
  availableInquiries,
  documents,
  personalInformation = [],
}: CreateDisputeDialogProps) {
  const [selectedType, setSelectedType] = useState<DisputeType>('account');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [reason, setReason] = useState('This information is inaccurate and needs to be corrected');
  const [supportingReason, setSupportingReason] = useState('');
  const [selectedRound, setSelectedRound] = useState<number>(1);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedInquiryIds, setSelectedInquiryIds] = useState<string[]>([]);
  
  // Account expansion and bureau selection
  const [expandedAccountId, setExpandedAccountId] = useState<string | null>(null);
  const [selectedBureaus, setSelectedBureaus] = useState<Record<string, Bureau[]>>({});
  
  // Account specific fields
  const [creditorName, setCreditorName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState('');
  const [dateOpened, setDateOpened] = useState('');
  const [accountStatus, setAccountStatus] = useState('');
  const [balance, setBalance] = useState('');
  
  // Personal Info specific fields
  const [expandedPersonalInfoField, setExpandedPersonalInfoField] = useState<string | null>(null);
  const [selectedPersonalInfoBureaus, setSelectedPersonalInfoBureaus] = useState<Record<string, Bureau[]>>({});
  
  // Account info popup state
  const [showAccountInfo, setShowAccountInfo] = useState<string | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ top: number; right: number } | null>(null);
  const infoButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // Close info popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showAccountInfo) {
        const target = event.target as HTMLElement;
        // Check if click is outside the popup and info button
        if (!target.closest('.info-popup') && !target.closest('.info-button')) {
          setShowAccountInfo(null);
        }
      }
    };

    if (showAccountInfo) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showAccountInfo]);

  useEffect(() => {
    if (error) {
      const timeoutId = setTimeout(() => {
        setError('');
      }, 10000);

      return () => clearTimeout(timeoutId);
    }
  }, [error]);

  const handleAccountSelect = (accountId: string) => {
    // Toggle expansion
    if (expandedAccountId === accountId) {
      setExpandedAccountId(null);
    } else {
      setExpandedAccountId(accountId);
      // Initialize with all bureaus selected if not already set
      if (!selectedBureaus[accountId]) {
        const account = accounts.find(acc => acc.id === accountId);
        if (account) {
          setSelectedBureaus(prev => ({
            ...prev,
            [accountId]: [Bureau.TRANSUNION, Bureau.EXPERIAN, Bureau.EQUIFAX]
          }));
        }
      }
    }
    
    const account = accounts.find(acc => acc.id === accountId);
    if (account) {
      setSelectedAccountId(accountId);
      setCreditorName(account.creditorName);
      setAccountNumber(account.accountNumber);
      setAccountType(account.accountType);
      setDateOpened(account.dateOpened);
      setAccountStatus(account.accountStatus);
      setBalance(account.currentBalance);
    }
  };

  const toggleBureauSelection = (accountId: string, bureau: Bureau) => {
    setSelectedBureaus(prev => {
      const currentBureaus = prev[accountId] || [];
      const isSelected = currentBureaus.includes(bureau);
      
      if (isSelected) {
        return {
          ...prev,
          [accountId]: currentBureaus.filter(b => b !== bureau)
        };
      } else {
        return {
          ...prev,
          [accountId]: [...currentBureaus, bureau]
        };
      }
    });
  };

  const handlePersonalInfoFieldSelect = (field: string, bureauValues: { tu: string; ex: string; eq: string }) => {
    // Toggle expansion
    if (expandedPersonalInfoField === field) {
      setExpandedPersonalInfoField(null);
    } else {
      setExpandedPersonalInfoField(field);
      // Initialize with only bureaus that have data
      if (!selectedPersonalInfoBureaus[field]) {
        const availableBureaus: Bureau[] = [];
        if (bureauValues.tu !== '-') availableBureaus.push(Bureau.TRANSUNION);
        if (bureauValues.ex !== '-') availableBureaus.push(Bureau.EXPERIAN);
        if (bureauValues.eq !== '-') availableBureaus.push(Bureau.EQUIFAX);
        
        setSelectedPersonalInfoBureaus(prev => ({
          ...prev,
          [field]: availableBureaus
        }));
      }
    }
  };

  const togglePersonalInfoBureauSelection = (field: string, bureau: Bureau) => {
    setSelectedPersonalInfoBureaus(prev => {
      const currentBureaus = prev[field] || [];
      const isSelected = currentBureaus.includes(bureau);
      
      if (isSelected) {
        return {
          ...prev,
          [field]: currentBureaus.filter(b => b !== bureau)
        };
      } else {
        return {
          ...prev,
          [field]: [...currentBureaus, bureau]
        };
      }
    });
  };

  // Build personal info fields from analytics data
  const buildPersonalInfoFields = () => {
    const personalInfoByBureau: { [key: string]: PersonalInformation } = {};
    personalInformation.forEach(info => {
      const bureauName = info.bureau.toLowerCase();
      if (bureauName.includes('transunion') || bureauName.includes('tu')) {
        personalInfoByBureau['TransUnion'] = info;
      } else if (bureauName.includes('experian') || bureauName.includes('exp') || bureauName === 'ex') {
        personalInfoByBureau['Experian'] = info;
      } else if (bureauName.includes('equifax') || bureauName.includes('eq')) {
        personalInfoByBureau['Equifax'] = info;
      }
    });

    const getBureauData = (field: keyof PersonalInformation) => {
      const getValue = (val: any) => {
        if (!val || val === '' || (Array.isArray(val) && val.length === 0)) return '-';
        return val;
      };
      
      return {
        tu: getValue(personalInfoByBureau['TransUnion']?.[field]),
        ex: getValue(personalInfoByBureau['Experian']?.[field]),
        eq: getValue(personalInfoByBureau['Equifax']?.[field])
      };
    };

    const getFieldStatus = (field: keyof PersonalInformation): { label: string; color: 'red' | 'orange' | 'green' } => {
      const data = getBureauData(field);
      const values = [data.tu, data.ex, data.eq].filter(v => v !== '-');
      
      if (values.length === 0) return { label: 'Match', color: 'green' };
      
      const uniqueValues = Array.from(new Set(values));
      if (uniqueValues.length === 1) {
        return { label: 'Match', color: 'green' };
      } else if (uniqueValues.length === values.length) {
        return { label: 'Mismatch', color: 'red' };
      } else {
        return { label: 'Partial Match', color: 'orange' };
      }
    };

    const fields: Array<{ 
      field: string; 
      key: keyof PersonalInformation; 
      status: string; 
      statusColor: 'red' | 'orange' | 'green';
      bureauValues: { tu: string; ex: string; eq: string };
    }> = [];
    
    // Helper to check if all bureaus are missing data
    const hasAnyData = (bureauValues: { tu: string; ex: string; eq: string }) => {
      return bureauValues.tu !== '-' || bureauValues.ex !== '-' || bureauValues.eq !== '-';
    };
    
    // Name
    const nameData = getBureauData('name');
    if (hasAnyData(nameData)) {
      const nameStatus = getFieldStatus('name');
      fields.push({ field: 'Full Name', key: 'name', status: nameStatus.label, statusColor: nameStatus.color, bureauValues: nameData });
    }
    
    // DOB
    const dobData = getBureauData('dob');
    if (hasAnyData(dobData)) {
      const dobStatus = getFieldStatus('dob');
      fields.push({ field: 'Date of Birth', key: 'dob', status: dobStatus.label, statusColor: dobStatus.color, bureauValues: dobData });
    }
    
    // Current Address
    const addressData = getBureauData('currentAddress');
    if (hasAnyData(addressData)) {
      const addressStatus = getFieldStatus('currentAddress');
      fields.push({ field: 'Current Address', key: 'currentAddress', status: addressStatus.label, statusColor: addressStatus.color, bureauValues: addressData });
    }
    
    // Previous Addresses - get max count across bureaus
    const maxPrevAddresses = Math.max(
      personalInfoByBureau['TransUnion']?.previousAddresses?.length || 0,
      personalInfoByBureau['Experian']?.previousAddresses?.length || 0,
      personalInfoByBureau['Equifax']?.previousAddresses?.length || 0
    );
    
    for (let i = 0; i < maxPrevAddresses; i++) {
      const prevAddressData = {
        tu: personalInfoByBureau['TransUnion']?.previousAddresses?.[i] || '-',
        ex: personalInfoByBureau['Experian']?.previousAddresses?.[i] || '-',
        eq: personalInfoByBureau['Equifax']?.previousAddresses?.[i] || '-'
      };
      
      // Skip if all bureaus are missing this address
      if (!hasAnyData(prevAddressData)) continue;
      
      const values = [prevAddressData.tu, prevAddressData.ex, prevAddressData.eq].filter(v => v !== '-');
      const uniqueValues = Array.from(new Set(values));
      
      let status: { label: string; color: 'red' | 'orange' | 'green' };
      if (uniqueValues.length === 1) {
        status = { label: 'Match', color: 'green' };
      } else if (uniqueValues.length === values.length) {
        status = { label: 'Mismatch', color: 'red' };
      } else {
        status = { label: 'Partial Match', color: 'orange' };
      }
      
      fields.push({ field: `Previous Address ${i + 1}`, key: 'previousAddresses', status: status.label, statusColor: status.color, bureauValues: prevAddressData });
    }
    
    // Employment History
    const maxEmployers = Math.max(
      personalInfoByBureau['TransUnion']?.employmentHistory?.length || 0,
      personalInfoByBureau['Experian']?.employmentHistory?.length || 0,
      personalInfoByBureau['Equifax']?.employmentHistory?.length || 0
    );
    
    for (let i = 0; i < maxEmployers; i++) {
      const employmentData = {
        tu: personalInfoByBureau['TransUnion']?.employmentHistory?.[i]?.employer || '-',
        ex: personalInfoByBureau['Experian']?.employmentHistory?.[i]?.employer || '-',
        eq: personalInfoByBureau['Equifax']?.employmentHistory?.[i]?.employer || '-'
      };
      
      // Skip if all bureaus are missing this employer
      if (!hasAnyData(employmentData)) continue;
      
      fields.push({ field: `Employer ${i + 1}`, key: 'employmentHistory', status: 'Partial Match', statusColor: 'orange', bureauValues: employmentData });
    }
    
    return fields;
  };

  const personalInfoFields = buildPersonalInfoFields();

  const buildRequestBody = (): any => {
    const attachments = selectedDocuments.length > 0 ? selectedDocuments : undefined;
    const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);
    const accountAddress = selectedAccount?.address;

    if (selectedType === 'account') {
      const bureaus = selectedBureaus[selectedAccountId] || [Bureau.EQUIFAX, Bureau.EXPERIAN, Bureau.TRANSUNION];
      const request: MultiRoundAccountDisputeRequest = {
        round: selectedRound,
        reason,
        supportingReason: supportingReason || undefined,
        attachmentNames: attachments,
        bureauAccountDisputes: bureaus.map((bureau) => ({
          bureau,
          account: {
            id: selectedAccountId,
            creditorName,
            accountNumber,
            accountType,
            dateOpened,
            status: accountStatus,
            balance,
            address: accountAddress,
          },
        })),
      };
      return request;
    } else if (selectedType === 'inquiry') {
      const selectedInquiries = availableInquiries.filter(inq => selectedInquiryIds.includes(inq.id));
      const request: MultiRoundInquiryDisputeRequest = {
        round: selectedRound,
        reason,
        supportingReason: supportingReason || undefined,
        attachmentNames: attachments,
        bureauInquiryDisputes: selectedInquiries.map((inq) => ({
          bureau: inq.bureau as Bureau,
          inquiry: {
            id: inq.id,
            date: inq.date,
            company: inq.company,
          },
        })),
      };
      return request;
    } else if (selectedType === 'late') {
      const bureaus = selectedBureaus[selectedAccountId] || [Bureau.EQUIFAX, Bureau.EXPERIAN, Bureau.TRANSUNION];
      const request: MultiRoundLatesDisputeRequest = {
        round: selectedRound,
        reason,
        supportingReason: supportingReason || undefined,
        attachmentNames: attachments,
        bureauAccountDisputes: bureaus.map((bureau) => ({
          bureau,
          account: {
            id: selectedAccountId,
            creditorName,
            accountNumber,
            accountType,
            dateOpened,
            status: accountStatus,
            balance,
            address: accountAddress,
          },
        })),
      };
      return request;
    } else {
      // Personal Info dispute - map selected fields and bureaus to corrections
      const corrections: Array<{ bureau: Bureau; category: PersonalInfoCategory; value: string }> = [];
      
      Object.entries(selectedPersonalInfoBureaus).forEach(([field, bureaus]) => {
        if (bureaus.length > 0) {
          const category = field.includes('Address') ? 'Address' as PersonalInfoCategory : 
                          field.includes('Name') ? 'Name' as PersonalInfoCategory :
                          field.includes('Birth') ? 'DOB' as PersonalInfoCategory :
                          field.includes('Employer') ? 'Employment' as PersonalInfoCategory :
                          'Name' as PersonalInfoCategory;
          
          bureaus.forEach(bureau => {
            corrections.push({
              bureau,
              category,
              value: field
            });
          });
        }
      });
      
      const request: MultiRoundPersonalInfoDisputeRequest = {
        round: selectedRound,
        supportingReason: supportingReason || undefined,
        attachmentNames: attachments,
        corrections,
      };
      return request;
    }
  };

  const isCreateButtonDisabled = () => {
    if (isCreating) return true;
    if (selectedDocuments.length === 0) return true;

    if (selectedType === 'personal-info') {
      const hasSelectedBureaus = Object.values(selectedPersonalInfoBureaus).some(bureaus => bureaus.length > 0);
      return !hasSelectedBureaus || !supportingReason || !supportingReason.trim();
    }

    if (selectedType === 'account' || selectedType === 'late') {
      return !selectedAccountId || !reason || !reason.trim();
    }

    if (selectedType === 'inquiry') {
      return selectedInquiryIds.length === 0 || !reason || !reason.trim();
    }

    return !reason || !reason.trim();
  };

  const handleCreateDispute = async () => {
    try {
      setIsCreating(true);
      setError('');

      // Validation
      if (selectedType !== 'personal-info' && !reason.trim()) {
        setError('Reason is required');
        setIsCreating(false);
        return;
      }

      if (selectedType === 'account' || selectedType === 'late') {
        if (!selectedAccountId) {
          setError('Please select an account');
          setIsCreating(false);
          return;
        }
      }

      if (selectedType === 'inquiry') {
        if (selectedInquiryIds.length === 0) {
          setError('Please select at least one inquiry');
          setIsCreating(false);
          return;
        }
      }

      if (selectedType === 'personal-info') {
        const hasSelectedBureaus = Object.values(selectedPersonalInfoBureaus).some(bureaus => bureaus.length > 0);
        if (!hasSelectedBureaus) {
          setError('Please select at least one bureau for a personal information field');
          setIsCreating(false);
          return;
        }
        if (!supportingReason || !supportingReason.trim()) {
          setError('Please provide a dispute reason');
          setIsCreating(false);
          return;
        }
      }

      const requestBody = buildRequestBody();

      let createdDispute: any;
      if (selectedType === 'account') {
        createdDispute = await apiService.createMultiRoundAccountDispute(requestBody);
      } else if (selectedType === 'inquiry') {
        createdDispute = await apiService.createMultiRoundInquiryDispute(requestBody);
      } else if (selectedType === 'late') {
        createdDispute = await apiService.createMultiRoundLatesDispute(requestBody);
      } else {
        createdDispute = await apiService.createMultiRoundPersonalInfoDispute(requestBody);
      }

      if (createdDispute?.success === false) {
        setError(createdDispute.message || 'Failed to create dispute');
        setIsCreating(false);
        return;
      }

      // Notify parent and close
      onDisputeCreated();
      onOpenChange(false);

      // Reset form
      setReason('This information is inaccurate and needs to be corrected');
      setSupportingReason('');
      setSelectedRound(1);
      setSelectedDocuments([]);
      setSelectedAccountId('');
      setSelectedInquiryIds([]);
      setCreditorName('');
      setAccountNumber('');
      setAccountType('');
      setDateOpened('');
      setAccountStatus('');
      setBalance('');
      setExpandedPersonalInfoField(null);
      setSelectedPersonalInfoBureaus({});
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create dispute');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="!h-screen !max-w-none !translate-x-0 !translate-y-0 !top-0 !left-auto !right-0 !rounded-none !data-[state=closed]:slide-out-to-right !data-[state=open]:slide-in-from-right !flex !flex-col !justify-start !items-start !p-0 !gap-0 [&>button]:!right-6 [&>button]:!top-6"
        style={{ width: '840px' }}
      >
        <DialogHeader
          className="flex-shrink-0 w-full space-y-0"
          style={{ paddingLeft: '24px', paddingRight: '72px', paddingTop: '24px', paddingBottom: '24px' }}
        >
          <DialogTitle className="text-lg font-semibold">Create Multiround Review</DialogTitle>
        </DialogHeader>
        <div className="pt-2 pb-4 px-6 w-full flex-1 overflow-y-auto">
          {/* Step Navigation */}
          <div className="w-full mb-6 bg-gray-100 p-1" style={{ borderRadius: '12px' }}>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setSelectedType('account')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-all ${
                  selectedType === 'account' 
                    ? 'bg-white text-black shadow-sm' 
                    : 'bg-transparent text-gray-600 hover:text-gray-900'
                }`}
                style={{ borderRadius: '10px' }}
              >
                Account
              </button>
              <button
                type="button"
                onClick={() => setSelectedType('inquiry')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-all ${
                  selectedType === 'inquiry' 
                    ? 'bg-white text-black shadow-sm' 
                    : 'bg-transparent text-gray-600 hover:text-gray-900'
                }`}
                style={{ borderRadius: '12px' }}
              >
                Inquiry
              </button>
              <button
                type="button"
                onClick={() => setSelectedType('late')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-all ${
                  selectedType === 'late' 
                    ? 'bg-white text-black shadow-sm' 
                    : 'bg-transparent text-gray-600 hover:text-gray-900'
                }`}
                style={{ borderRadius: '12px' }}
              >
                Late Payments
              </button>
              <button
                type="button"
                onClick={() => setSelectedType('personal-info')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-all ${
                  selectedType === 'personal-info' 
                    ? 'bg-white text-black shadow-sm' 
                    : 'bg-transparent text-gray-600 hover:text-gray-900'
                }`}
                style={{ borderRadius: '12px' }}
              >
                Personal Info
              </button>
            </div>
          </div>

          {/* Account Tab */}
          {selectedType === 'account' && (
            <div className="space-y-6">
              {/* Section 1: Select Accounts */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">1</div>
                  <h3 className="text-base font-semibold">Select Accounts to Dispute</h3>
                </div>
                
                <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
                  <div className="space-y-2">
                    {accounts.length === 0 ? (
                      <div className="text-sm text-gray-500 py-8 text-center">
                        No accounts available
                      </div>
                    ) : (
                      accounts.map((account) => {
                        const isExpanded = expandedAccountId === account.id;
                        const accountBureaus = selectedBureaus[account.id] || [];
                        
                        return (
                          <div key={account.id} className={`border transition-all ${
                            isExpanded 
                              ? 'border-black' 
                              : 'border-gray-200'
                          }`} style={{ borderRadius: '8px' }}>
                            {/* Main Account Row */}
                            <div
                              onClick={() => handleAccountSelect(account.id)}
                              className={`cursor-pointer transition-all ${
                                isExpanded 
                                  ? 'border-b border-gray-200' 
                                  : 'hover:bg-gray-50'
                              }`}
                              style={{ height: '60px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '0 14px', gap: '16px' }}
                            >
                              <div className="flex items-center gap-3">
                                <div className="rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0" style={{ width: '16px', height: '16px' }}>
                                  {isExpanded && <div className="rounded-full bg-black" style={{ width: '8px', height: '8px' }} />}
                                </div>
                                <div className="flex-shrink-0 rounded-full bg-gray-100 flex items-center justify-center" style={{ width: '32px', height: '32px' }}>
                                  <CreditCard className="h-4 w-4 text-gray-600" />
                                </div>
                                <div className="min-w-0">
                                  <div className="font-medium text-sm">{account.creditorName}</div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {account.accountNumber}
                                  </div>
                                </div>
                              </div>
                              <div className="flex-shrink-0 flex items-center gap-2">
                                {account.accountStatus === 'Open' && (
                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 border border-green-600" style={{ borderRadius: '8px' }}>
                                    Open
                                  </span>
                                )}
                                {account.accountStatus === 'Closed' && (
                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 border border-gray-600" style={{ borderRadius: '8px' }}>
                                    Closed
                                  </span>
                                )}
                                <div className="relative">
                                  <button
                                    type="button"
                                    ref={(el) => { infoButtonRefs.current[account.id] = el; }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (showAccountInfo === account.id) {
                                        setShowAccountInfo(null);
                                        setPopupPosition(null);
                                      } else {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setPopupPosition({
                                          top: rect.bottom + 8,
                                          right: window.innerWidth - rect.right
                                        });
                                        setShowAccountInfo(account.id);
                                      }
                                    }}
                                    className="info-button flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100 transition-colors"
                                  >
                                    <Info className="h-[18px] w-[18px] text-gray-500" />
                                  </button>
                                  
                                  {/* Account Info Popup - Rendered via Portal */}
                                  {showAccountInfo === account.id && popupPosition && createPortal(
                                    <div 
                                      className="info-popup fixed z-[9999] w-[280px] bg-white border border-gray-200 shadow-lg p-4 space-y-3"
                                      style={{ 
                                        borderRadius: '8px',
                                        top: `${popupPosition.top}px`,
                                        right: `${popupPosition.right}px`
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                        <div>
                                          <div className="text-xs text-gray-500">Creditor Name</div>
                                          <div className="text-sm text-gray-900">{account.creditorName}</div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-gray-500">Date Opened</div>
                                          <div className="text-sm text-gray-900">{account.dateOpened || 'N/A'}</div>
                                        </div>
                                        
                                        <div>
                                          <div className="text-xs text-gray-500">Account Type</div>
                                          <div className="text-sm text-gray-900">{account.accountType || 'N/A'}</div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-gray-500">Status</div>
                                          <div className="text-sm text-gray-900">{account.accountStatus || 'N/A'}</div>
                                        </div>
                                        
                                        <div>
                                          <div className="text-xs text-gray-500">Account Number</div>
                                          <div className="text-sm text-gray-900">{account.accountNumber}</div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-gray-500">Balance</div>
                                          <div className="text-sm text-gray-900">{account.currentBalance || '$0.00'}</div>
                                        </div>
                                      </div>
                                    </div>,
                                    document.body
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Expanded Bureau Rows */}
                            {isExpanded && (() => {
                              const bureauVariants = accountsGroupedById.get(account.id) || [];
                              const tuAccount = bureauVariants.find(acc => acc.bureau === 'TransUnion');
                              const exAccount = bureauVariants.find(acc => acc.bureau === 'Experian');
                              const eqAccount = bureauVariants.find(acc => acc.bureau === 'Equifax');
                              
                              return (
                              <div className="px-3 pt-2 pb-2 space-y-1">
                                {[
                                  { bureau: Bureau.TRANSUNION, label: 'TU', bureauAccount: tuAccount },
                                  { bureau: Bureau.EXPERIAN, label: 'EX', bureauAccount: exAccount },
                                  { bureau: Bureau.EQUIFAX, label: 'EQ', bureauAccount: eqAccount }
                                ].map(({ bureau, label, bureauAccount }) => {
                                  const isBureauSelected = accountBureaus.includes(bureau);
                                  const status = bureauAccount?.accountStatus || account.accountStatus;
                                  const balance = bureauAccount?.currentBalance || account.currentBalance;
                                  return (
                                    <div
                                      key={bureau}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleBureauSelection(account.id, bureau);
                                      }}
                                      className={`cursor-pointer transition-all ${
                                        isBureauSelected ? 'bg-gray-50' : 'bg-white'
                                      }`}
                                      style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '0 14px', gap: '16px' }}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={`border flex items-center justify-center flex-shrink-0 ${
                                          isBureauSelected ? 'border-black bg-black' : 'border-gray-300'
                                        }`} style={{ width: '18px', height: '18px', borderRadius: '5px' }}>
                                          {isBureauSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                                        </div>
                                        <div className="flex items-center justify-center flex-shrink-0 bg-white border border-gray-300 text-gray-700 text-xs font-semibold" style={{ width: '32px', height: '24px', borderRadius: '6px' }}>
                                          {label}
                                        </div>
                                        <div className="min-w-0">
                                          <div className="font-medium text-sm">{account.creditorName}</div>
                                          <div className="text-xs text-gray-600 mt-1">
                                            {status} • {balance || '$0.00'}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              );
                            })()}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2: Select Dispute Reason */}
              {selectedAccountId && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">2</div>
                    <h3 className="text-base font-semibold">Select Dispute Reason</h3>
                  </div>
                  
                  <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
                    <div className="space-y-2">
                      {[
                        { 
                          value: 'I want dispute the accuracy of this account',
                          label: 'I want to dispute the accuracy of this account',
                          description: 'Account information appears to be inaccurate, and I request a review'
                        },
                        { 
                          value: 'This isn\'t my account or I never authorized it',
                          label: 'This isn\'t my account or I never authorized it',
                          description: 'Account doesn\'t belong to you or was opened without permission.'
                        },
                        { 
                          value: 'This negative account was listed twice or re-aged to look recent',
                          label: 'This negative account was listed twice or re-aged to look recent',
                          description: 'The same delinquency appears multiple times or shows a new start date.'
                        },
                        { 
                          value: 'The creditor couldn\'t confirm this information during investigation',
                          label: 'The creditor couldn\'t confirm this information during investigation',
                          description: 'The CRA or furnisher failed to provide proof within 30 days.'
                        },
                        { 
                          value: 'I actually paid on time, the late mark or charge-off is a mistake',
                          label: 'I actually paid on time, the late mark or charge-off is a mistake',
                          description: 'The creditor misreported an on-time or settled account as late or delinquent.'
                        },
                        { 
                          value: 'The date, balance, or payment status is incorrect',
                          label: 'The date, balance, or payment status is incorrect',
                          description: 'Payment dates, amounts, or current status don\'t match your records.'
                        },
                        { 
                          value: 'This belongs to someone else or was opened using my personal info',
                          label: 'This belongs to someone else or was opened using my personal info',
                          description: 'Someone else\'s account appears in your report or identity theft occurred.'
                        },
                        { 
                          value: 'This account is over seven years old and should be removed',
                          label: 'This account is over seven years old and should be removed',
                          description: 'The delinquency date exceeds seven years (except certain bankruptcies).'
                        },
                        { 
                          value: 'This account was discharged or settled',
                          label: 'This account was discharged or settled',
                          description: 'An account still shows negative marks after a bankruptcy discharge.'
                        }
                      ].map((option) => {
                        const isSelected = reason === option.value;
                        return (
                          <div
                            key={option.value}
                            onClick={() => setReason(option.value)}
                            className={`border cursor-pointer transition-all ${
                              isSelected 
                                ? 'border-black bg-gray-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr', alignItems: 'center', padding: '0 14px' }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0" style={{ width: '16px', height: '16px' }}>
                                {isSelected && <div className="rounded-full bg-black" style={{ width: '8px', height: '8px' }} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium">{option.label}</div>
                                <div className="text-xs text-gray-600 mt-1">{option.description}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Supporting Reason (Optional)</label>
                    <textarea
                      className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                      rows={3}
                      placeholder="Provide any additional information about your dispute"
                      value={supportingReason}
                      onChange={(e) => setSupportingReason(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Section 3: Select Required Documents */}
              {selectedAccountId && reason && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">3</div>
                    <h3 className="text-base font-semibold">Select Required Documents</h3>
                  </div>
                  
                  <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
                    <div className="space-y-2">
                      {documents.length === 0 ? (
                        <div className="text-sm text-gray-500 py-8 text-center">
                          No documents available
                        </div>
                      ) : (
                        documents.map((doc) => {
                          const isSelected = selectedDocuments.includes(doc.documentName);
                          return (
                            <div
                              key={doc.id}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedDocuments(selectedDocuments.filter((name) => name !== doc.documentName));
                                } else {
                                  setSelectedDocuments([...selectedDocuments, doc.documentName]);
                                }
                              }}
                              className={`border cursor-pointer transition-all ${
                                isSelected 
                                  ? 'border-black bg-gray-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr', alignItems: 'center', padding: '0 14px' }}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`border flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? 'border-black bg-black' : 'border-gray-300'
                                }`} style={{ width: '14px', height: '14px', borderRadius: '3px' }}>
                                  {isSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium">{doc.documentName}</div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {(doc.fileSize / 1024).toFixed(0)} KB • {new Date(doc.createdAt).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Inquiry Tab */}
          {selectedType === 'inquiry' && (
            <div className="space-y-6">
              {/* Section 1: Select Inquiries */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">1</div>
                  <h3 className="text-base font-semibold">Select Inquiries to Dispute</h3>
                </div>
                
                <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
                  <div className="space-y-2">
                    {availableInquiries.length === 0 ? (
                      <div className="text-sm text-gray-500 py-8 text-center">
                        No inquiries available
                      </div>
                    ) : (
                      availableInquiries.map((inquiry) => {
                        const isSelected = selectedInquiryIds.includes(inquiry.id);
                        return (
                          <div
                            key={inquiry.id}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedInquiryIds(selectedInquiryIds.filter((id) => id !== inquiry.id));
                              } else {
                                setSelectedInquiryIds([...selectedInquiryIds, inquiry.id]);
                              }
                            }}
                            className={`border cursor-pointer transition-all ${
                              isSelected 
                                ? 'border-black bg-gray-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '0 14px', gap: '16px' }}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`border flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'border-black bg-black' : 'border-gray-300'
                              }`} style={{ width: '14px', height: '14px', borderRadius: '3px' }}>
                                {isSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm">{inquiry.company}</div>
                                <div className="text-xs text-gray-600 mt-1">
                                  Date: {inquiry.date}
                                </div>
                              </div>
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-2">
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800" style={{ borderRadius: '8px' }}>
                                {inquiry.bureau}
                              </span>
                              <div className="relative">
                                <button
                                  type="button"
                                  ref={(el) => { infoButtonRefs.current[inquiry.id] = el; }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (showAccountInfo === inquiry.id) {
                                      setShowAccountInfo(null);
                                      setPopupPosition(null);
                                    } else {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setPopupPosition({
                                        top: rect.bottom + 8,
                                        right: window.innerWidth - rect.right
                                      });
                                      setShowAccountInfo(inquiry.id);
                                    }
                                  }}
                                  className="info-button flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100 transition-colors"
                                >
                                  <Info className="h-[18px] w-[18px] text-gray-500" />
                                </button>
                                
                                {/* Inquiry Info Popup - Rendered via Portal */}
                                {showAccountInfo === inquiry.id && popupPosition && (() => {
                                  // Calculate expires in (inquiries stay for 2 years)
                                  const inquiryDate = new Date(inquiry.date);
                                  const expirationDate = new Date(inquiryDate);
                                  expirationDate.setFullYear(expirationDate.getFullYear() + 2);
                                  const today = new Date();
                                  const daysRemaining = Math.ceil((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                                  const monthsRemaining = Math.floor(daysRemaining / 30);
                                  const expiresIn = daysRemaining > 0 
                                    ? monthsRemaining > 0 
                                      ? `${monthsRemaining} month${monthsRemaining !== 1 ? 's' : ''}`
                                      : `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`
                                    : 'Expired';
                                  
                                  return createPortal(
                                    <div 
                                      className="info-popup fixed z-[9999] w-[280px] bg-white border border-gray-200 shadow-lg p-4 space-y-3"
                                      style={{ 
                                        borderRadius: '8px',
                                        top: `${popupPosition.top}px`,
                                        right: `${popupPosition.right}px`
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                        <div>
                                          <div className="text-xs text-gray-500">Company</div>
                                          <div className="text-sm text-gray-900">{inquiry.company}</div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-gray-500">Date</div>
                                          <div className="text-sm text-gray-900">{inquiry.date}</div>
                                        </div>
                                        
                                        <div>
                                          <div className="text-xs text-gray-500">Bureau</div>
                                          <div className="text-sm text-gray-900">{inquiry.bureau}</div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-gray-500">Expires In</div>
                                          <div className="text-sm text-gray-900">{expiresIn}</div>
                                        </div>
                                      </div>
                                    </div>,
                                    document.body
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2: Select Dispute Reason */}
              {selectedInquiryIds.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">2</div>
                    <h3 className="text-base font-semibold">Select Dispute Reason</h3>
                  </div>
                  
                  <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
                    <div className="space-y-2">
                      {[
                        { 
                          value: 'You never applied for credit or authorized the company',
                          label: 'You never applied for credit or authorized the company',
                          description: 'I didn\'t give permission for this inquiry'
                        },
                        { 
                          value: 'This inquiry was made by mistake or for the wrong reason',
                          label: 'This inquiry was made by mistake or for the wrong reason',
                          description: 'A company pulled your report without a valid purpose'
                        },
                        { 
                          value: 'The same application caused multiple inquiries',
                          label: 'The same application caused multiple inquiries',
                          description: 'This inquiry shows up more than once'
                        },
                        { 
                          value: 'I already disputed this, but it wasn\'t fixed',
                          label: 'I already disputed this, but it wasn\'t fixed',
                          description: 'The bureau or lender didn\'t remove the inquiry after your first dispute'
                        },
                        { 
                          value: 'I just want to confirm this inquiry is correct',
                          label: 'I just want to confirm this inquiry is correct',
                          description: 'You recognize the company but want verification and minimal impact'
                        }
                      ].map((option) => {
                        const isSelected = reason === option.value;
                        return (
                          <div
                            key={option.value}
                            onClick={() => setReason(option.value)}
                            className={`border cursor-pointer transition-all ${
                              isSelected 
                                ? 'border-black bg-gray-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr', alignItems: 'center', padding: '0 14px' }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0" style={{ width: '16px', height: '16px' }}>
                                {isSelected && <div className="rounded-full bg-black" style={{ width: '8px', height: '8px' }} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium">{option.label}</div>
                                <div className="text-xs text-gray-600 mt-1">{option.description}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Supporting Reason (Optional)</label>
                    <textarea
                      className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                      rows={3}
                      placeholder="Provide any additional information about your dispute"
                      value={supportingReason}
                      onChange={(e) => setSupportingReason(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Section 3: Select Required Documents */}
              {selectedInquiryIds.length > 0 && reason && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">3</div>
                    <h3 className="text-base font-semibold">Select Required Documents</h3>
                  </div>
                  
                  <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
                    <div className="space-y-2">
                      {documents.length === 0 ? (
                        <div className="text-sm text-gray-500 py-8 text-center">
                          No documents available
                        </div>
                      ) : (
                        documents.map((doc) => {
                          const isSelected = selectedDocuments.includes(doc.documentName);
                          return (
                            <div
                              key={doc.id}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedDocuments(selectedDocuments.filter((name) => name !== doc.documentName));
                                } else {
                                  setSelectedDocuments([...selectedDocuments, doc.documentName]);
                                }
                              }}
                              className={`border cursor-pointer transition-all ${
                                isSelected 
                                  ? 'border-black bg-gray-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr', alignItems: 'center', padding: '0 14px' }}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`border flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? 'border-black bg-black' : 'border-gray-300'
                                }`} style={{ width: '14px', height: '14px', borderRadius: '3px' }}>
                                  {isSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium">{doc.documentName}</div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {(doc.fileSize / 1024).toFixed(0)} KB • {new Date(doc.createdAt).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Late Payments Tab */}
          {selectedType === 'late' && (
            <div className="space-y-6">
              {/* Section 1: Select Accounts */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">1</div>
                  <h3 className="text-base font-semibold">Select Accounts to Dispute</h3>
                </div>
                
                <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
                  <div className="space-y-2">
                    {accounts.length === 0 ? (
                      <div className="text-sm text-gray-500 py-8 text-center">
                        No accounts available
                      </div>
                    ) : (
                      accounts.map((account) => {
                        const isExpanded = expandedAccountId === account.id;
                        const accountBureaus = selectedBureaus[account.id] || [];
                        
                        return (
                          <div key={account.id} className={`border transition-all ${
                            isExpanded 
                              ? 'border-black' 
                              : 'border-gray-200'
                          }`} style={{ borderRadius: '8px' }}>
                            {/* Main Account Row */}
                            <div
                              onClick={() => handleAccountSelect(account.id)}
                              className={`cursor-pointer transition-all ${
                                isExpanded 
                                  ? 'border-b border-gray-200' 
                                  : 'hover:bg-gray-50'
                              }`}
                              style={{ height: '60px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '0 14px', gap: '16px' }}
                            >
                              <div className="flex items-center gap-3">
                                <div className="rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0" style={{ width: '16px', height: '16px' }}>
                                  {isExpanded && <div className="rounded-full bg-black" style={{ width: '8px', height: '8px' }} />}
                                </div>
                                <div className="flex-shrink-0 rounded-full bg-gray-100 flex items-center justify-center" style={{ width: '32px', height: '32px' }}>
                                  <CreditCard className="h-4 w-4 text-gray-600" />
                                </div>
                                <div className="min-w-0">
                                  <div className="font-medium text-sm">{account.creditorName}</div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {account.accountNumber}
                                  </div>
                                </div>
                              </div>
                              <div className="flex-shrink-0 flex items-center gap-2">
                                {account.accountStatus === 'Open' && (
                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 border border-green-600" style={{ borderRadius: '8px' }}>
                                    Open
                                  </span>
                                )}
                                {account.accountStatus === 'Closed' && (
                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 border border-gray-600" style={{ borderRadius: '8px' }}>
                                    Closed
                                  </span>
                                )}
                                <div className="relative">
                                  <button
                                    type="button"
                                    ref={(el) => { infoButtonRefs.current[account.id] = el; }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (showAccountInfo === account.id) {
                                        setShowAccountInfo(null);
                                        setPopupPosition(null);
                                      } else {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setPopupPosition({
                                          top: rect.bottom + 8,
                                          right: window.innerWidth - rect.right
                                        });
                                        setShowAccountInfo(account.id);
                                      }
                                    }}
                                    className="info-button flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100 transition-colors"
                                  >
                                    <Info className="h-[18px] w-[18px] text-gray-500" />
                                  </button>
                                  
                                  {/* Account Info Popup - Rendered via Portal */}
                                  {showAccountInfo === account.id && popupPosition && createPortal(
                                    <div 
                                      className="info-popup fixed z-[9999] w-[280px] bg-white border border-gray-200 shadow-lg p-4 space-y-3"
                                      style={{ 
                                        borderRadius: '8px',
                                        top: `${popupPosition.top}px`,
                                        right: `${popupPosition.right}px`
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                        <div>
                                          <div className="text-xs text-gray-500">Creditor Name</div>
                                          <div className="text-sm text-gray-900">{account.creditorName}</div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-gray-500">Date Opened</div>
                                          <div className="text-sm text-gray-900">{account.dateOpened || 'N/A'}</div>
                                        </div>
                                        
                                        <div>
                                          <div className="text-xs text-gray-500">Account Type</div>
                                          <div className="text-sm text-gray-900">{account.accountType || 'N/A'}</div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-gray-500">Status</div>
                                          <div className="text-sm text-gray-900">{account.accountStatus || 'N/A'}</div>
                                        </div>
                                        
                                        <div>
                                          <div className="text-xs text-gray-500">Account Number</div>
                                          <div className="text-sm text-gray-900">{account.accountNumber}</div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-gray-500">Balance</div>
                                          <div className="text-sm text-gray-900">{account.currentBalance || '$0.00'}</div>
                                        </div>
                                      </div>
                                    </div>,
                                    document.body
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Expanded Bureau Rows */}
                            {isExpanded && (() => {
                              const bureauVariants = accountsGroupedById.get(account.id) || [];
                              const tuAccount = bureauVariants.find(acc => acc.bureau === 'TransUnion');
                              const exAccount = bureauVariants.find(acc => acc.bureau === 'Experian');
                              const eqAccount = bureauVariants.find(acc => acc.bureau === 'Equifax');
                              
                              return (
                              <div className="px-3 pt-2 pb-2 space-y-1">
                                {[
                                  { bureau: Bureau.TRANSUNION, label: 'TU', bureauAccount: tuAccount },
                                  { bureau: Bureau.EXPERIAN, label: 'EX', bureauAccount: exAccount },
                                  { bureau: Bureau.EQUIFAX, label: 'EQ', bureauAccount: eqAccount }
                                ].map(({ bureau, label, bureauAccount }) => {
                                  const isBureauSelected = accountBureaus.includes(bureau);
                                  const status = bureauAccount?.accountStatus || account.accountStatus;
                                  const balance = bureauAccount?.currentBalance || account.currentBalance;
                                  return (
                                    <div
                                      key={bureau}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleBureauSelection(account.id, bureau);
                                      }}
                                      className={`cursor-pointer transition-all ${
                                        isBureauSelected ? 'bg-gray-50' : 'bg-white'
                                      }`}
                                      style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '0 14px', gap: '16px' }}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={`border flex items-center justify-center flex-shrink-0 ${
                                          isBureauSelected ? 'border-black bg-black' : 'border-gray-300'
                                        }`} style={{ width: '14px', height: '14px', borderRadius: '3px' }}>
                                          {isBureauSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                                        </div>
                                        <div className="flex items-center justify-center flex-shrink-0 bg-white border border-gray-300 text-gray-700 text-xs font-semibold" style={{ width: '32px', height: '24px', borderRadius: '6px' }}>
                                          {label}
                                        </div>
                                        <div className="min-w-0">
                                          <div className="font-medium text-sm">{account.creditorName}</div>
                                          <div className="text-xs text-gray-600 mt-1">
                                            {status} • {balance || '$0.00'}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              );
                            })()}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2: Select Dispute Reason */}
              {selectedAccountId && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">2</div>
                    <h3 className="text-base font-semibold">Select Dispute Reason</h3>
                  </div>
                  
                  <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
                    <div className="space-y-2">
                      {[
                        { 
                          value: 'The late payment is wrong or never happened',
                          label: 'The late payment is wrong or never happened',
                          description: 'The payment was on time or misreported.'
                        },
                        { 
                          value: 'The account shows a late payment I already fixed or paid',
                          label: 'The account shows a late payment I already fixed or paid',
                          description: 'The payment issue was resolved but still appears on your report.'
                        },
                        { 
                          value: 'This account was affected by a system or posting delay',
                          label: 'This account was affected by a system or posting delay',
                          description: 'The creditor posted payment late even though it was made on time.'
                        },
                        { 
                          value: 'The late payment looks duplicated or dated incorrectly',
                          label: 'The late payment looks duplicated or dated incorrectly',
                          description: 'The same late mark appears more than once or was re-aged to look newer.'
                        },
                        { 
                          value: 'This is not my account or I was a victim of fraud',
                          label: 'This is not my account or I was a victim of fraud',
                          description: 'The account doesn\'t belong to you or was opened without permission.'
                        },
                        { 
                          value: 'I\'ve disputed this before but it wasn\'t corrected',
                          label: 'I\'ve disputed this before but it wasn\'t corrected',
                          description: 'A previous correction request was ignored or mishandled.'
                        }
                      ].map((option) => {
                        const isSelected = reason === option.value;
                        return (
                          <div
                            key={option.value}
                            onClick={() => setReason(option.value)}
                            className={`border cursor-pointer transition-all ${
                              isSelected 
                                ? 'border-black bg-gray-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr', alignItems: 'center', padding: '0 14px' }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0" style={{ width: '16px', height: '16px' }}>
                                {isSelected && <div className="rounded-full bg-black" style={{ width: '8px', height: '8px' }} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium">{option.label}</div>
                                <div className="text-xs text-gray-600 mt-1">{option.description}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Supporting Reason (Optional)</label>
                    <textarea
                      className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                      rows={3}
                      placeholder="Provide any additional information about your dispute"
                      value={supportingReason}
                      onChange={(e) => setSupportingReason(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Section 3: Select Required Documents */}
              {selectedAccountId && reason && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">3</div>
                    <h3 className="text-base font-semibold">Select Required Documents</h3>
                  </div>
                  
                  <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
                    <div className="space-y-2">
                      {documents.length === 0 ? (
                        <div className="text-sm text-gray-500 py-8 text-center">
                          No documents available
                        </div>
                      ) : (
                        documents.map((doc) => {
                          const isSelected = selectedDocuments.includes(doc.documentName);
                          return (
                            <div
                              key={doc.id}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedDocuments(selectedDocuments.filter((name) => name !== doc.documentName));
                                } else {
                                  setSelectedDocuments([...selectedDocuments, doc.documentName]);
                                }
                              }}
                              className={`border cursor-pointer transition-all ${
                                isSelected 
                                  ? 'border-black bg-gray-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr', alignItems: 'center', padding: '0 14px' }}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`border flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? 'border-black bg-black' : 'border-gray-300'
                                }`} style={{ width: '14px', height: '14px', borderRadius: '3px' }}>
                                  {isSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium">{doc.documentName}</div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {(doc.fileSize / 1024).toFixed(0)} KB • {new Date(doc.createdAt).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Personal Info Tab */}
          {selectedType === 'personal-info' && (
            <div className="space-y-6">
              {/* Section 1: Select Personal Information to Dispute */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">1</div>
                  <h3 className="text-base font-semibold">Select Personal Information to Dispute</h3>
                </div>
                
                <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
                  <div className="space-y-2">
                    {personalInfoFields.length === 0 ? (
                      <div className="text-sm text-gray-500 py-8 text-center">
                        No personal information available
                      </div>
                    ) : (
                      personalInfoFields.map((item, index) => {
                      const isExpanded = expandedPersonalInfoField === item.field;
                      const fieldBureaus = selectedPersonalInfoBureaus[item.field] || [];
                      
                      return (
                        <div key={index} className={`border transition-all ${
                          isExpanded 
                            ? 'border-black' 
                            : 'border-gray-200'
                        }`} style={{ borderRadius: '8px' }}>
                          {/* Main Field Row */}
                          <div
                            onClick={() => handlePersonalInfoFieldSelect(item.field, item.bureauValues)}
                            className={`cursor-pointer transition-all ${
                              isExpanded 
                                ? 'border-b border-gray-200' 
                                : 'hover:bg-gray-50'
                            }`}
                            style={{ height: '60px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '0 14px', gap: '16px' }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0" style={{ width: '16px', height: '16px' }}>
                                {isExpanded && <div className="rounded-full bg-black" style={{ width: '8px', height: '8px' }} />}
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium text-sm">{item.field}</div>
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              <span className={`inline-flex items-center px-2 py-1 text-xs font-medium ${
                                item.statusColor === 'red' ? 'text-red-600 border border-red-600' :
                                item.statusColor === 'orange' ? 'text-orange-600 border border-orange-600' :
                                'text-green-600 border border-green-600'
                              }`} style={{ borderRadius: '8px' }}>
                                {item.status}
                              </span>
                            </div>
                          </div>

                          {/* Expanded Bureau Rows */}
                          {isExpanded && (
                            <div className="px-3 pt-2 pb-2 space-y-1">
                              {[
                                { bureau: Bureau.TRANSUNION, label: 'TU', valueKey: 'tu' as const },
                                { bureau: Bureau.EXPERIAN, label: 'EX', valueKey: 'ex' as const },
                                { bureau: Bureau.EQUIFAX, label: 'EQ', valueKey: 'eq' as const }
                              ].filter(({ valueKey }) => item.bureauValues[valueKey] !== '-').map(({ bureau, label, valueKey }) => {
                                const isBureauSelected = fieldBureaus.includes(bureau);
                                const bureauValue = item.bureauValues[valueKey];
                                return (
                                  <div
                                    key={bureau}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      togglePersonalInfoBureauSelection(item.field, bureau);
                                    }}
                                    className={`cursor-pointer transition-all ${
                                      isBureauSelected ? 'bg-gray-50' : 'bg-white'
                                    }`}
                                    style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '0 14px', gap: '16px' }}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`border flex items-center justify-center flex-shrink-0 ${
                                        isBureauSelected ? 'border-black bg-black' : 'border-gray-300'
                                      }`} style={{ width: '14px', height: '14px', borderRadius: '3px' }}>
                                        {isBureauSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                                      </div>
                                      <div className="flex items-center justify-center flex-shrink-0 bg-white border border-gray-300 text-gray-700 text-xs font-semibold" style={{ width: '32px', height: '24px', borderRadius: '6px' }}>
                                        {label}
                                      </div>
                                      <div className="min-w-0">
                                        <div className="font-medium text-sm">{bureauValue}</div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2: Provide Dispute Reason */}
              {Object.values(selectedPersonalInfoBureaus).some(bureaus => bureaus.length > 0) && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">2</div>
                    <h3 className="text-base font-semibold">Provide Dispute Reason</h3>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Dispute Reason</label>
                    <textarea
                      className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                      rows={4}
                      placeholder="Provide reason about your dispute"
                      value={supportingReason}
                      onChange={(e) => setSupportingReason(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Bottom Action Bar */}
        <div className="w-full bg-white border-t border-gray-200">
          <div className="px-6 py-2 flex items-center justify-between gap-4">
            {error ? (
              <p className="text-sm text-red-600 flex-1">{error}</p>
            ) : (
              <div />
            )}
            <Button 
              onClick={handleCreateDispute} 
              disabled={isCreateButtonDisabled()} 
              className="h-10 px-6 bg-gray-800 hover:bg-gray-900 text-white font-medium flex-shrink-0"
              style={{ borderRadius: '8px' }}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Review...
                </>
              ) : (
                'Create Review'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
